import { selectServerLastCommunication, useAppSelector } from '@/store';
import { ConnectionIndicator, Button, UndoIcon, RedoIcon, ChevronDownStroke } from '@repo/atomic-components';
import { classNames } from 'primereact/utils';
import { useParams } from 'react-router';
import { useUndoRedo } from '../hooks';
import { useGdprControlsState } from '../GdprControlsContext';
import { useColumnSize } from '../ColumnSizeContext';

import { FC, SVGProps, useEffect, useRef, useState } from 'react';

/**
 * ColumnDropdownIcon
 * SVG icon for column layout selector button
 * Shows a grid icon representing different column layouts
 */
type ColumnCount = 1 | 2 | 3 | 4;

/**
 * ColumnsLayoutIcon
 * Renders N evenly-spaced page rectangles to visually represent a column layout.
 * Used inside the column-selector dropdown so users can preview each option.
 */
const ColumnsLayoutIcon: FC<{ columns: ColumnCount }> = ({ columns }) => {
  const totalWidth = 32;
  const height = 22;
  const gap = 2;
  const colWidth = (totalWidth - (columns - 1) * gap) / columns;
  return (
    <svg
      width={totalWidth}
      height={height}
      viewBox={`0 0 ${totalWidth} ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {Array.from({ length: columns }).map((_, i) => (
        <rect
          key={i}
          x={i * (colWidth + gap)}
          y={0}
          width={colWidth}
          height={height}
          rx={3}
          fill="currentColor"
          fillOpacity={0.7}
        />
      ))}
    </svg>
  );
};

const ColumnDropdownIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M19 21.5H16C14.895 21.5 14 20.605 14 19.5V14.5C14 13.395 14.895 12.5 16 12.5H19C20.105 12.5 21 13.395 21 14.5V19.5C21 20.605 20.105 21.5 19 21.5Z"
      stroke="#323232"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M19 9.5H16C14.895 9.5 14 8.605 14 7.5V5.5C14 4.395 14.895 3.5 16 3.5H19C20.105 3.5 21 4.395 21 5.5V7.5C21 8.605 20.105 9.5 19 9.5Z"
      stroke="#323232"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5 3.5H8C9.105 3.5 10 4.395 10 5.5V10.5C10 11.605 9.105 12.5 8 12.5H5C3.895 12.5 3 11.605 3 10.5V5.5C3 4.395 3.895 3.5 5 3.5Z"
      stroke="#323232"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5 15.5H8C9.105 15.5 10 16.395 10 17.5V19.5C10 20.605 9.105 21.5 8 21.5H5C3.895 21.5 3 20.605 3 19.5V17.5C3 16.395 3.895 15.5 5 15.5Z"
      stroke="#323232"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ActionsPanel: FC = () => {
  // Redux state selectors
  const serverLastCommunication = useAppSelector(selectServerLastCommunication); // For auto-save indicator
  // V2-local column-size state (kept out of the shared gdprColumnSize slice so V2's
  // 4-column option can't leak into V1). Drives the dropdown's active-option highlight.
  const { columnSize: currentColumnSize, setColumnSize } = useColumnSize();

  // Router hooks
  const { documentId } = useParams<{ documentId: string }>(); // Current document ID from URL

  /**
   * Undo/Redo Functionality
   * Manages undo/redo state for page selection changes
   */
  const { canUndo, canRedo, isUndoing, isRedoing, handleUndo, handleRedo } = useUndoRedo({ documentId });

  // Custom column-selector dropdown — built in-place rather than using PrimeReact's Menu
  // so we can render an SVG layout preview next to each option and right-align the panel.
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);
  const columnMenuContainerRef = useRef<HTMLDivElement>(null);

  // Close the dropdown on outside click
  useEffect(() => {
    if (!isColumnMenuOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (columnMenuContainerRef.current && !columnMenuContainerRef.current.contains(event.target as Node)) {
        setIsColumnMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isColumnMenuOpen]);

  const handleColumnIconClick = () => setIsColumnMenuOpen((open) => !open);
  const handleColumnSelect = (count: ColumnCount) => {
    setColumnSize(count);
    setIsColumnMenuOpen(false);
  };

  // Page jumper — moved from the sidebar. Uses source-document page numbers
  // (Page.originalPageNumber) so it matches the number rendered under each PDF page.
  const { currentOriginalPageNumber, maxOriginalPageNumber, onGoToOriginalPageNumber, totalPages } =
    useGdprControlsState();
  const pageInputRef = useRef<HTMLInputElement>(null);
  const handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || !pageInputRef.current) return;
    if (totalPages === 0 || maxOriginalPageNumber === 0) return;
    const raw = parseInt(pageInputRef.current.value, 10);
    if (Number.isNaN(raw)) {
      pageInputRef.current.value = String(currentOriginalPageNumber);
      return;
    }
    const clamped = Math.min(Math.max(raw, 1), maxOriginalPageNumber);
    pageInputRef.current.value = String(clamped);
    onGoToOriginalPageNumber(clamped);
  };

  return (
    <div className="bg-fletchers-white border-fletchers-gray-110 flex h-full w-full flex-row items-center gap-2 border-t border-b border-t-transparent py-[7px] pr-2 pl-6">
      {/*
                LEFT SECTION: Page title + auto-save indicator
                Case reference + document name moved to the right-hand Sidebar in V2.
            */}
      <div className="flex items-center gap-2">
        <div className="text-xl font-semibold text-gray-900">GDPR Check</div>
        {/* Auto-save indicator - shows last successful sync time. 8px margin on all sides. */}
        <div className="m-2">
          <ConnectionIndicator lastCommunication={serverLastCommunication} />
        </div>
      </div>

      {/*
                CENTER SECTION: Undo/Redo Controls
                Styling matches the full-bundle BundleUndoRedoButtons (separator via undo's right border).
            */}
      {/*
          Undo/Redo buttons — copied verbatim from BundlePage's BundleUndoRedoButtons,
          with 14x14 icons (defaults are 16x16) and wired to V2's useUndoRedo hook.
      */}
      <div
        className={classNames(
          'border-fletchers-gray-110 flex h-full max-h-[32px] overflow-hidden rounded-[4px] border',
        )}
      >
        <Button
          icon={<UndoIcon className="h-[14px] w-[14px] -translate-y-px text-black" />}
          onClick={handleUndo}
          outlined
          className="!border-r-fletchers-gray-110 !h-[32px] !w-[32px] rounded-none! !border-0 !border-r !border-solid !px-[9px] !py-[9px] hover:!bg-gray-50"
          disabled={!canUndo || isUndoing}
        />
        <Button
          icon={<RedoIcon className="h-[14px] w-[14px] -translate-y-px text-black" />}
          onClick={handleRedo}
          outlined
          className="!h-[32px] !w-[32px] rounded-none! !border-0 !px-[9px] !py-[9px] hover:!bg-gray-50"
          disabled={!canRedo || isRedoing}
        />
      </div>

      {/*
                RIGHT SECTION: Page jumper + Column Layout Selector.
                Page jumper sits to the left of the column-grid dropdown.
            */}
      <div className="ml-auto flex items-center justify-end gap-2">
        {/* Page jumper — uses source-document page numbers (Page.originalPageNumber).
            Border color matches the column-dropdown button (border-fletchers-gray-110). */}
        <div
          className={classNames(
            'h-[32px] px-2',
            'flex items-center justify-center gap-1.5',
            'bg-fletchers-white border-fletchers-gray-110 rounded-sm border text-sm',
          )}
        >
          <span>Page</span>
          <input
            ref={pageInputRef}
            name="pageNumberInput"
            type="number"
            min="1"
            max={maxOriginalPageNumber || undefined}
            step="1"
            data-max-digits={maxOriginalPageNumber.toString().length}
            defaultValue={currentOriginalPageNumber.toString()}
            key={currentOriginalPageNumber}
            onKeyDown={handlePageInputKeyDown}
            className="border-fletchers-gray-110 bg-fletchers-white inline-block w-12 rounded-sm border-1 px-1 focus:outline-offset-0 focus:outline-none"
          />
          <span>of</span>
          <span>{maxOriginalPageNumber}</span>
        </div>

        <div ref={columnMenuContainerRef} className="relative">
          <Button outlined onClick={handleColumnIconClick} className="!h-[32px] !gap-2 hover:!bg-gray-50">
            <ColumnDropdownIcon width={18} height={18} />
            {/* ChevronDownStroke's path actually points up, so flip 180° to get a downward chevron */}
            <ChevronDownStroke className="rotate-180 text-black" />
          </Button>

          {isColumnMenuOpen && (
            // Right-aligned to the trigger button. Styling mirrors PrimeReact menus used elsewhere
            // in the app (white surface, fletchers-gray border, subtle shadow, rounded corners).
            <div
              role="menu"
              className="border-fletchers-gray-110 absolute top-full right-0 z-20 mt-1 w-[200px] overflow-hidden rounded-md border bg-white shadow-lg"
            >
              {([1, 2, 3, 4] as ColumnCount[]).map((count) => {
                const isActive = currentColumnSize === count;
                return (
                  <button
                    key={count}
                    type="button"
                    role="menuitemradio"
                    aria-checked={isActive}
                    onClick={() => handleColumnSelect(count)}
                    className={classNames(
                      'flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left text-sm transition-colors',
                      isActive
                        ? 'bg-fletchers-green-100 text-fletchers-green-500 font-semibold'
                        : 'text-gray-800 hover:bg-gray-100',
                    )}
                  >
                    {/* Icon inherits text-color via SVG `currentColor`. Wrap unselected
                        rows so the icon goes lighter grey while the label keeps its
                        gray-800 contrast. Selected rows inherit fletchers-green-500
                        from the button itself. */}
                    <span className={isActive ? undefined : 'text-gray-400'}>
                      <ColumnsLayoutIcon columns={count} />
                    </span>
                    <span>
                      {count} {count === 1 ? 'column' : 'columns'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
